import AppConfig from "@/core/config/AppConfig";
import BaseApiService from "./BaseApiService";
import JwtService from "../JwtService";
import ServiceConstants from "./ServiceConstants";
import store from "@/store";
import { Actions } from "@/store/enums/StoreEnums";
import Pagination from '@/models/common/Pagination'
import NotificationDialog from "@/core/helpers/notificationDialog";

class OrchApiService extends BaseApiService {
    private static client = new BaseApiService()
        .Build(AppConfig.Api.Orchestrator.BaseUrl);
    private static authenticatedClient = () => {
        const token = JwtService.getToken()
        return new BaseApiService(token)
            .Build(AppConfig.Api.Orchestrator.BaseUrl);
    }
    private static authenticatedClientForFile = () => {
        const token = JwtService.getToken()
        return new BaseApiService(token, true)
            .Build(AppConfig.Api.Orchestrator.BaseUrl);
    }


    public static Configuration = {
        Robot: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Robot.RobotListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetForSelect: function () {
                return BaseApiService.GetListForSelectbox(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Robot.RobotListPath(1, 1000, null))
            },
            GetForSelectForCompany: function (companyId: string, onlyRobots: boolean) {
                if (onlyRobots)
                    return BaseApiService.GetListForSelectbox(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Robot.RobotListPath(1, 1000, { companyId: companyId, type: 0 }));

                return BaseApiService.GetListForSelectbox(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Robot.RobotListPath(1, 1000, { companyId: companyId }));
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Robot.RobotDetailPath(id))
            },
            Create: function (entity: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Robot.Name, entity).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("robotList", "editRobot", data.data.id)
                    }
                })
            },
            UpdateDetails: function (entity: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Robot.RobotUpdateDetailsPath(entity.id), entity).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            UpdateCredentials: function (entity: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Robot.RobotUpdateCredentialsPath(entity.id), entity).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            UpdateLicenses: function (entity: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Robot.RobotUpdateLicensesPath(entity.robotId), entity).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            UpdateState: function (id: string, status: number) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), `${ServiceConstants.Orchestrator.Configuration.Robot.RobotDetailPath(id)}/status/${status}`)
            },
            UnbindMachine: function (robotId: string) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), `${ServiceConstants.Orchestrator.Configuration.Robot.RobotDetailPath(robotId)}/deactivateMachine`)
            },
            Delete: function (robotId: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), `${ServiceConstants.Orchestrator.Configuration.Robot.RobotDetailPath(robotId)}`)
            }
        },
        Environment: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Environment.EnvironmentListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Environment.EnvironmentDetailPath(id))
            },
            GetWithRobotsById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), `${ServiceConstants.Orchestrator.Configuration.Environment.EnvironmentDetailPath(id)}/withRobots`)
            },
            Create: function (entity: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Environment.Name, entity).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("environmentList", "editEnvironment", data.data.id)
                    }
                })
            },
            Update: function (entity: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Environment.EnvironmentDetailPath(entity.id), entity).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            UpdateRobots: function (entity: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), `${ServiceConstants.Orchestrator.Configuration.Environment.EnvironmentDetailPath(entity.id)}/robots`, entity.robots)
            },
            Delete: function (environmentId: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Configuration.Environment.EnvironmentDetailPath(environmentId));
            }
        }
    }

    public static Communication = {
        SupportRequest: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequest.SupportRequestListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequest.SupportRequestDetailPath(id))
            },
            Create: function (model: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequest.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("supportRequestList", "viewSupportRequest", data.data.id)
                    }
                })
            },
            Start: function (id: string) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequest.SupportRequestStartPath(id))
            },
            Resolve: function (id: string) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequest.SupportRequestResolvePath(id))
            },
            Reject: function (id: string) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequest.SupportRequestRejectPath(id))
            },
            Cancel: function (id: string) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequest.SupportRequestCancelPath(id))
            },
            Reply: function (model: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequest.SupportRequestReplyPath(model.supportRequestId), model, false)
            },
        },
        SupportRequestCategory: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequestCategory.SupportRequestCategoryListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetForSelect: function () {
                return BaseApiService.GetListForSelectbox(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequestCategory.SupportRequestCategoryListPath(1, 1000, null))
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequestCategory.SupportRequestCategoryDetailPath(id))
            },
            Create: function (model: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequestCategory.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("supportRequestCategoryList", "editSupportRequestCategory", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequestCategory.SupportRequestCategoryDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Communication.SupportRequestCategory.SupportRequestCategoryDetailPath(id))
            },
        }
    }

    public static Settings = {
        ApplicationClient: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.ApplicationClient.ApplicationClientListPath(pagination.page!, pagination.pageSize, filters))
            },
            Create: function (model: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.ApplicationClient.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("applicationClientList", "editApplicationClient", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.ApplicationClient.ApplicationClientDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.ApplicationClient.ApplicationClientDetailPath(id))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.ApplicationClient.ApplicationClientDetailPath(id));
            }
        },
        Language: {
            GetAll: function () {
                return OrchApiService.authenticatedClient().get(ServiceConstants.Orchestrator.Settings.Language.Name + '/all').catch((err) => {
                    throw new Error(err);
                })
            },
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Language.LanguageListPath(pagination.page!, pagination.pageSize, filters))
            },
            Create: function (model: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Language.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("languageList", "editLanguage", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Language.LanguageDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Language.LanguageDetailPath(id))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Language.LanguageDetailPath(id));
            }
        },
        MailAccount: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.MailAccount.MailAccountListPath(pagination.page!, pagination.pageSize, filters))
            },
            Create: function (model: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.MailAccount.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("mailAccountList", "editMailAccount", data.data.id)
                    }
                })
            },
            Update: function (id: string, model: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.MailAccount.MailAccountDetailPath(id), model)
                    .then(() => {
                        NotificationDialog.Notify("UpdatedSuccessfully")
                    })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.MailAccount.MailAccountDetailPath(id))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.MailAccount.MailAccountDetailPath(id));
            }
        },
        MailTemplate: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.MailTemplate.MailTemplateListPath(pagination.page!, pagination.pageSize, filters))
            },
            Create: function (model: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClientForFile(), ServiceConstants.Orchestrator.Settings.MailTemplate.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("mailTemplateList", "editMailTemplate", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClientForFile(), ServiceConstants.Orchestrator.Settings.MailTemplate.MailTemplateDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.MailTemplate.MailTemplateDetailPath(id))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.MailTemplate.MailTemplateDetailPath(id));
            }
        },
        NonWorkingDay: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.NonWorkingDay.NonWorkingDayListPath(pagination.page!, pagination.pageSize, filters))
            },
            GetForSelct: function () {
                return BaseApiService.GetListForSelectbox(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.NonWorkingDay.NonWorkingDayListPath(1, 1000, null))
            },
            GetForSelectForCompany: function (companyId: string) {
                return BaseApiService.GetListForSelectbox(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.NonWorkingDay.NonWorkingDayListPath(1, 1000, { companyId: companyId }));
            },
            Create: function (model: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.NonWorkingDay.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("nonWorkingDayList", "editNonWorkingDay", data.data.id)
                    }
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.NonWorkingDay.NonWorkingDayDetailPath(id))
            },
            GeWithItemsById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), `${ServiceConstants.Orchestrator.Settings.NonWorkingDay.NonWorkingDayDetailPath(id)}/withItems`)
            },
            UpdateItems: function (id: string, items: Array<any>) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), `${ServiceConstants.Orchestrator.Settings.NonWorkingDay.NonWorkingDayDetailPath(id)}/days`, items).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Update: function (model) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), `${ServiceConstants.Orchestrator.Settings.NonWorkingDay.NonWorkingDayDetailPath(model.id)}`, model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.NonWorkingDay.NonWorkingDayDetailPath(id));
            }
        },
        Parameter: {
            GetListPaginated: function (pagination: Pagination, filters: any) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Parameter.ParameterListPath(pagination.page!, pagination.pageSize, filters))
            },
            Create: function (model: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Parameter.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("parameterList", "editParameter", data.data.id)
                    }
                })
            },
            Update: function (model: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Parameter.ParameterDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Parameter.ParameterDetailPath(id))
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Parameter.ParameterDetailPath(id))
            }
        },
        Translation: {
            getWord: function (key: string) {
                return OrchApiService.client.get(ServiceConstants.Orchestrator.Settings.Translation.GetWordPath(key)).catch((err) => {
                    store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
                    throw new Error(err);
                })
            },
            getAllTranslations: function () {
                return OrchApiService.client.get(ServiceConstants.Orchestrator.Settings.Translation.AllTranslationsListPath()).catch((err) => {
                    throw new Error(err);
                })
            },
            GetList: function (pagination: Pagination, filters: any = null) {
                return BaseApiService.GetList(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Translation.TranslationListPath(pagination.page!, pagination.pageSize, filters))
            },
            Create: function (model: any) {
                return BaseApiService.Create(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Translation.Name, model).then(({ data }) => {
                    if (data.data) {
                        NotificationDialog.SuccessPrompt("translationList", "editTranslation", data.data.id)
                    }
                })
            },
            GetById: function (id: string) {
                return BaseApiService.GetById(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Translation.TranslationDetailPath(id))
            },
            Update: function (model: any) {
                return BaseApiService.Update(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Translation.TranslationDetailPath(model.id), model).then(() => {
                    NotificationDialog.Notify("UpdatedSuccessfully")
                })
            },
            Delete: function (id: string) {
                return BaseApiService.Delete(OrchApiService.authenticatedClient(), ServiceConstants.Orchestrator.Settings.Translation.TranslationDetailPath(id))
            },
        }
    }
}

export default OrchApiService;